import styled from "@emotion/styled";
import { useHeadsUpClick } from "@/components/heads-up/context/heads-up-context";
import { SmartNoteRow } from "@/components/heads-up/cards/common/smart-note-row";
import { NoteList, SmartCardSection } from "@/components/heads-up/cards/common/layout";
import { CollapsibleSectionHeader } from "@/components/heads-up/cards/common/collapsible-section-header";
import { useIncrementalReveal } from "@/components/heads-up/cards/common/use-incremental-reveal";
import type { FC } from "react";
import type { Maybe } from "@/domains/common/types";
import type { NoteListSmartCard } from "@/components/heads-up/heads-up.types";
import { SeeMoreButton } from "@/components/heads-up/cards/common/see-more-button";
import type { SmartNotePreviewInfo } from "@/components/heads-up/cards/common/smart-note-preview.types";

type Props = {
  card: NoteListSmartCard;
  getNotePreviewInfo?: (noteId: string) => Maybe<SmartNotePreviewInfo>;
};

export const NoteListSmartCardRenderer: FC<Props> = ({ card, getNotePreviewInfo }) => {
  const clickHandlers = useHeadsUpClick();

  const sectionTitle = card.value.title;
  const items = card.value.items.map(item => ({
    id: item.note_id,
    primary: item.primary_label ?? "Untitled",
    secondary: item.secondary_label,
  }));

  const { visibleItems, hasMore, showAll, reset } = useIncrementalReveal({
    cardId: card.id,
    items,
    initialCount: 3,
    step: 5,
  });

  return (
    <SmartCardSection>
      <CollapsibleSectionHeader title={sectionTitle} onCollapseChange={c => c && reset()}>
        <NoteList>
          {visibleItems.map(item => (
            <SmartNoteRow
              key={item.id}
              noteId={item.id}
              primary={item.primary}
              secondary={item.secondary || undefined}
              onClick={clickHandlers?.noteClick(item.id)}
              notePreviewInfo={getNotePreviewInfo?.(item.id)}
            />
          ))}

          <SeeMoreButton hasMore={hasMore} onClick={showAll} />
        </NoteList>
      </CollapsibleSectionHeader>
    </SmartCardSection>
  );
};

export const SectionTitle = styled.span``;
